import * as THREE from 'three';
import { WIND_GLSL, WindField } from './wind';
import { SCOTOPIC_ROD_W, SCOTOPIC_TINT_N, nightFactor } from './exposure';

// The night sky's stars: the universe's catalog, drawn as points at the sky radius.
//
// Not a skybox. The server sends the catalog once (universe positions, luminosities, colours) and, in
// every sun message, the world's own universe position and local frame. A star's direction is
// (star - world) rotated into the local frame, so the stars turn with the day and shift with the
// year (parallax), and a star's brightness is luminosity / distance^2 dimmed and reddened by the
// atmosphere along its own air mass, the same way the light field treats it.
//
// Everything per star happens in the vertex shader; the CPU only uploads a few uniforms per frame.
//
// Shimmer. Stars twinkle because turbulent cells high in the air refract the light, and the cells
// sweep across the sight line on the wind aloft. What the eye sees is not a pulse: it is an irregular,
// fast flicker with no period, brief flashes and dips of brightness (log-normal, so more dips than
// flashes), a flicker in colour that grows with the air mass, and a faint dance in position. Each star
// has its own noise, since stars even a few arcseconds apart see unrelated cells. The shared wind
// field sets the pace (its strength scales the shimmer clock, which is accumulated on the CPU so a
// change in wind never jumps the phase) and the gust band over the camera swells and calms the depth.
// Low stars cross more air and so flicker harder in all three. Cosmetic, like the wind itself: ground
// wind stands in for the wind aloft, and the positional dance is exaggerated to a fraction of a pixel.
//
// Brightness. The unit is the home star's flux at the world (1.0). A star is a Gaussian spot a fraction
// of a pixel wide, so its energy lands in a few pixels: peak equivalent illumination
// E = F / (2 sigma^2 omega) with omega the solid angle of one pixel. That is the same kind of quantity
// as the sky's irradiance, so a star goes through the renderer's own sky curve, 1 - exp(-SKY_EXPOSURE *
// gain * E), and is blended on the sky the way the two would add in linear light (a screen blend).
// Contrast with the sky then comes out right without a magnitude cutoff: moonless, a magnitude 6 star
// shows; under a full moon only the first few magnitudes do.

export interface StarsMessage {
  type: 'stars';
  count: number;
  record_bytes: number;
  magnitude_of_unit_flux: number;
  magnitude_limit: number;
  extinction: [number, number, number];
  data: string;                     // base64 of zlib of `count` records (see broadcast_server.py)
}

export interface StarViewState {
  worldPos: ArrayLike<number>;      // world position in universe space
  frame: ArrayLike<number>;         // 3x3 row-major: east, north, up in universe space
}

const STAR_RECORD_BYTES = 20;

// Tunables.
export const STAR_SIGMA_CSS  = 0.42;   // Gaussian spread of a star, in CSS pixels, so it keeps its on-screen size when the
                                       // render resolution steps down
export const STAR_SIGMA_MIN  = 0.55;   // floor in framebuffer pixels: narrower than this, a star flickers as it crosses pixels
export const STAR_GAIN       = 0.55;   // overall brightness of stars against the sky (1 = the sky's own curve)
export const STAR_TWINKLE    = 1.0;    // scintillation strength (0 = steady); stronger near the horizon
export const STAR_SHIMMER_RATE_STILL = 0.01; // twinkle speed in still air, relative to 1 at the wind field's nominal strength
export const STAR_JITTER     = 0.01;    // positional dance of low stars (0 = none)
export const STAR_MIN_GAIN   = 300;    // below this exposure gain (daylight, dusk) stars are not drawn at all
export const STAR_SCOT_MAX   = 0.35;   // most a star's colour is pulled toward the rods' cool grey, at night, for the faintest stars
const STAR_SCOT_FAINT_LO     = 0.03;   // star peak (display units, before blend) below which it counts as fully faint
const STAR_SCOT_FAINT_HI     = 0.45;   // ... and above which it is bright enough for cones and keeps its catalogue colour
const TONE_BASE_EXPOSURE     = 1.3;    // must match the renderer's BASE_EXPOSURE (the night colour blend keys off base * gain)
const SKY_EXPOSURE           = 6.0;    // must match the renderer's skylight -> colour curve

const SHIM_WRAP_S = 2048;            // must match SHIM_WRAP in the shader

const rad = (d: number) => d * Math.PI / 180;

const VERT = /* glsl */ `
${WIND_GLSL}
uniform vec2  uCamXZ;           // camera position in three's x-z, for the gust band overhead
uniform vec3  uWorldPos;
uniform mat3  uFrame;           // rows east, north, up: local = uFrame * universe_direction
uniform vec3  uTau;             // atmosphere zenith optical depth per channel
uniform float uSkyR;
uniform float uPixOmega;        // solid angle of one framebuffer pixel
uniform float uSigma;
uniform float uGain;            // the renderer's exposure gain
uniform float uGainScale;       // SKY_EXPOSURE * STAR_GAIN
uniform float uShimmerT;        // accumulated shimmer clock, seconds at nominal wind (wraps at SHIM_WRAP)
uniform vec2  uPixNdc;          // size of one framebuffer pixel in normalised device coordinates
uniform float uJitter;
uniform float uTwinkle;
uniform float uScot;            // night factor 0..1 (see exposure.ts); scaled per star below
uniform float uScotMax;
uniform float uFaintLo;
uniform float uFaintHi;
uniform vec3  uRodW;
uniform vec3  uRodTint;

attribute vec3  aColor;
attribute float aLum;
attribute float aSeed;

varying vec3  vX;               // per-channel optical strength at the star's centre, scintillation included
varying vec3  vBase;            // the same without scintillation
varying vec3  vScint;           // the scintillation factor per channel
varying float vSize;
varying float vSigma;

const float SHIM_WRAP = 2048.0;

float hash11(float p) {
  p = fract(p * 0.1031);
  p *= p + 33.33;
  p *= p + p;
  return fract(p);
}
// Smooth 1D noise in -1..1 (standard deviation 0.5) at a given lattice rate in cells per second.
// The lattice index wraps at SHIM_WRAP seconds, so the clock can wrap without a jump.
float shimNoise(float T, float rate, float key) {
  float x = T * rate;
  float P = SHIM_WRAP * rate;
  float i0 = mod(floor(x), P);
  float i1 = mod(i0 + 1.0, P);
  float f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  return mix(hash11(i0 + key), hash11(i1 + key), f) * 2.0 - 1.0;
}

void main() {
  vec3 d = position - uWorldPos;              // float32 is ample: the direction error is ~1e-7 rad
  float dist = length(d);
  vec3 loc = uFrame * (d / dist);             // east, north, up
  float elev = loc.z;
  if (elev < -0.03) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 0.0; vX = vec3(0.0); vBase = vec3(0.0); vScint = vec3(1.0); vSize = 0.0; vSigma = 1.0; return; }

  // Kasten & Young air mass, held at its horizon value just below the horizon.
  float zdeg = 90.0 - degrees(asin(clamp(max(elev, 0.0), 0.0, 1.0)));
  float am = 1.0 / (cos(radians(zdeg)) + 0.50572 * pow(96.07995 - zdeg, -1.6364));
  vec3 trans = exp(-uTau * am);

  // Direction in three's axes (sim x,y,z -> three x,z,-y).
  vec3 dirT = vec3(loc.x, loc.z, -loc.y);

  // Shimmer. Three octaves of noise at roughly 7, 15 and 31 cells per second (at nominal wind) give
  // an aperiodic flicker; z has unit standard deviation. The intensity factor is log-normal with unit
  // mean. Colour flickers independently per channel, more with air mass; position dances a little.
  float T = uShimmerT;
  float k0 = aSeed * 997.0;
  float z = (shimNoise(T, 7.0, k0) + 0.7 * shimNoise(T, 15.0, k0 + 311.0) + 0.5 * shimNoise(T, 31.0, k0 + 571.0)) / 0.65;
  float depth = 0.75 + 0.5 * windGust(uCamXZ);                 // a gust overhead agitates the sky
  float tw = min(0.6, 0.15 + 0.03 * am) * uTwinkle * depth;
  float chroma = clamp(0.02 * (am - 1.0), 0.0, 0.30) * uTwinkle;
  vec3 zc = vec3(shimNoise(T, 17.0, k0 + 101.0), shimNoise(T, 19.0, k0 + 733.0), shimNoise(T, 23.0, k0 + 947.0)) * 2.0;
  vec3 scint = exp(tw * z + chroma * zc - 0.5 * (tw * tw + chroma * chroma));
  float jit = min(1.0, 0.20 * sqrt(am)) * uJitter * uTwinkle * depth;
  vec2 dance = jit * 2.0 * vec2(shimNoise(T, 9.0, k0 + 17.0) + 0.6 * shimNoise(T, 21.0, k0 + 421.0),
                                shimNoise(T, 11.0, k0 + 389.0) + 0.6 * shimNoise(T, 25.0, k0 + 811.0));

  // Energy per channel reaching the eye.
  vec3 eBase = (aLum / (dist * dist)) * trans * aColor;
  vec3 e = eBase * scint;
  float toDisp = uGainScale * uGain / (2.0 * uSigma * uSigma * uPixOmega);

  // Night colour. Rods wash the colour out of faint stars, but a bright star is seen by the cones too and
  // keeps its hue (Sirius blue-white, Arcturus orange, Antares red). So the pull toward the cool rod grey
  // used for the sky and ground (exposure.ts) is partial, and fades out as the star gets brighter. The
  // flicker loses its colour in the same proportion.
  float peak = toDisp * max(eBase.r, max(eBase.g, eBase.b));
  float k = uScot * uScotMax * (1.0 - smoothstep(uFaintLo, uFaintHi, peak));
  eBase = mix(eBase, dot(eBase, uRodW) * uRodTint, k);
  e     = mix(e,     dot(e,     uRodW) * uRodTint, k);
  vec3 scintShown = mix(scint, vec3(dot(scint, uRodW)), k);

  vX = toDisp * e;
  vScint = scintShown;
  vBase = toDisp * eBase;
  float xmax = max(vX.r, max(vX.g, vX.b));
  if (xmax < 0.004) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 0.0; vSize = 0.0; vSigma = 1.0; return; }

  // The spot is drawn out to where it falls below about 1 percent, so bright stars grow a little.
  float r = uSigma * sqrt(2.0 * log(max(xmax / 0.01, 1.0)));
  vSize = clamp(2.0 * r + 2.0, 3.0, 48.0);
  vSigma = uSigma;
  gl_PointSize = vSize;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(dirT * uSkyR, 1.0);
  gl_Position.xy += dance * uPixNdc * gl_Position.w;
}
`;

const FRAG = /* glsl */ `
uniform float uSrgbOut;
varying vec3  vX;
varying vec3  vBase;
varying vec3  vScint;
varying float vSize;
varying float vSigma;

vec3 encodeSrgb(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}

void main() {
  vec2 p = (gl_PointCoord - 0.5) * vSize;
  float g = exp(-dot(p, p) / (2.0 * vSigma * vSigma));
  vec3 s = 1.0 - exp(-vX * g);                 // the sky's own curve, per channel
  // A bright star sits on the flat top of that curve, where a change in its flux barely moves the
  // display. Apply the flicker to the displayed value too, in proportion to how saturated the star is,
  // so bright stars dip and flash instead of staying a steady dot. A faint star is on the linear part
  // of the curve and already flickers through its flux.
  vec3 sat = 1.0 - exp(-vBase * g);
  s = min(s * mix(vec3(1.0), vScint, sat), vec3(1.0));
  if (uSrgbOut > 0.5) s = encodeSrgb(s);
  gl_FragColor = vec4(s, 1.0);                 // blended as a screen: dst + src * (1 - dst)
}
`;

async function inflate(b64: string): Promise<Uint8Array> {
  const bin = atob(b64);
  const raw = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) raw[i] = bin.charCodeAt(i);
  const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export class StarField {
  readonly object: THREE.Points;
  private geometry = new THREE.BufferGeometry();
  private material: THREE.ShaderMaterial;
  private frame = new THREE.Matrix3();
  private loaded = false;
  private loadSeq = 0;
  /** Stars in the catalog message (0 until it arrives). */
  count = 0;

  private wind: WindField;
  private lastT = -1;
  private shimmerT = 0;

  constructor(skyRadius: number, wind: WindField) {
    this.wind = wind;
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcColorFactor,
      uniforms: {
        ...wind.uniforms,
        uCamXZ:     { value: new THREE.Vector2() },
        uWorldPos:  { value: new THREE.Vector3() },
        uFrame:     { value: this.frame },
        uTau:       { value: new THREE.Vector3(0.1, 0.15, 0.27) },
        uSkyR:      { value: skyRadius },
        uPixOmega:  { value: 1.7e-6 },
        uSigma:     { value: STAR_SIGMA_MIN },
        uGain:      { value: 1 },
        uGainScale: { value: SKY_EXPOSURE * STAR_GAIN },
        uShimmerT:  { value: 0 },
        uPixNdc:    { value: new THREE.Vector2(0.0016, 0.0028) },
        uJitter:    { value: STAR_JITTER },
        uTwinkle:   { value: STAR_TWINKLE },
        uSrgbOut:   { value: 1 },
        uScot:      { value: 0 },
        uScotMax:   { value: STAR_SCOT_MAX },
        uFaintLo:   { value: STAR_SCOT_FAINT_LO },
        uFaintHi:   { value: STAR_SCOT_FAINT_HI },
        uRodW:      { value: new THREE.Vector3(...SCOTOPIC_ROD_W) },
        uRodTint:   { value: new THREE.Vector3(...SCOTOPIC_TINT_N) },
      },
    });
    this.object = new THREE.Points(this.geometry, this.material);
    this.object.frustumCulled = false;       // positions are universe coordinates, not sky positions
    this.object.visible = false;
  }

  /** Decode a `stars` message. Async (the payload is zlib-compressed); a newer message wins. */
  async load(msg: StarsMessage): Promise<void> {
    const seq = ++this.loadSeq;
    const bytes = await inflate(msg.data);
    if (seq !== this.loadSeq) return;
    const n = Math.min(msg.count, Math.floor(bytes.byteLength / STAR_RECORD_BYTES));
    const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const pos = new Float32Array(n * 3), lum = new Float32Array(n), col = new Float32Array(n * 3), seed = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const o = i * STAR_RECORD_BYTES;
      pos[i * 3] = dv.getFloat32(o, true); pos[i * 3 + 1] = dv.getFloat32(o + 4, true); pos[i * 3 + 2] = dv.getFloat32(o + 8, true);
      lum[i] = dv.getFloat32(o + 12, true);
      col[i * 3] = bytes[o + 16] / 255; col[i * 3 + 1] = bytes[o + 17] / 255; col[i * 3 + 2] = bytes[o + 18] / 255;
      seed[i] = (i * 0.61803398875) % 1;
    }
    this.geometry.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.geometry.setAttribute('aLum', new THREE.BufferAttribute(lum, 1));
    this.geometry.setAttribute('aColor', new THREE.BufferAttribute(col, 3));
    this.geometry.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    this.geometry.setDrawRange(0, n);
    const ext = msg.extinction ?? [0.1, 0.15, 0.27];
    (this.material.uniforms.uTau.value as THREE.Vector3).set(ext[0], ext[1], ext[2]);
    this.count = n;
    this.loaded = true;
  }

  /** Per frame. `view` null (an older server with no frame) hides the stars. `pixelRatio` is the
   *  renderer's current framebuffer pixels per CSS pixel, which sets the spot's width. */
  update(camera: THREE.PerspectiveCamera, view: StarViewState | null, gain: number, timeS: number,
         drawingBufferHeight: number, srgbOut: boolean, pixelRatio: number) {
    const visible = this.loaded && !!view && gain >= STAR_MIN_GAIN;
    this.object.visible = visible;
    if (!visible || !view) return;
    const u = this.material.uniforms;
    this.object.position.copy(camera.position);             // camera-locked: the sky does not parallax
    (u.uWorldPos.value as THREE.Vector3).set(view.worldPos[0], view.worldPos[1], view.worldPos[2]);
    const f = view.frame;
    this.frame.set(f[0], f[1], f[2], f[3], f[4], f[5], f[6], f[7], f[8]);
    const pixAng = 2 * Math.tan(rad(camera.fov) / 2) / Math.max(1, drawingBufferHeight);
    u.uPixOmega.value = pixAng * pixAng;
    u.uSigma.value = Math.max(STAR_SIGMA_MIN, STAR_SIGMA_CSS * pixelRatio);
    u.uGain.value = gain;
    u.uScot.value = nightFactor(TONE_BASE_EXPOSURE * gain);
    // The shimmer clock runs at a rate that follows the wind's strength; accumulating it keeps the
    // phase continuous when the wind changes.
    const dt = this.lastT < 0 ? 0 : Math.max(0, Math.min(timeS - this.lastT, 0.25));
    this.lastT = timeS;
    const amp = this.wind.uniforms.uWindAmp.value;
    this.shimmerT = (this.shimmerT + dt * (STAR_SHIMMER_RATE_STILL + (1 - STAR_SHIMMER_RATE_STILL) * amp)) % SHIM_WRAP_S;
    u.uShimmerT.value = this.shimmerT;
    (u.uPixNdc.value as THREE.Vector2).set(2 / Math.max(1, drawingBufferHeight * camera.aspect), 2 / Math.max(1, drawingBufferHeight));
    (u.uCamXZ.value as THREE.Vector2).set(camera.position.x, camera.position.z);
    u.uSrgbOut.value = srgbOut ? 1 : 0;
  }

  dispose() {
    this.geometry.dispose();
    this.material.dispose();
  }
}
